import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FileSearch, Handshake, TrendingUp, CheckCircle } from 'lucide-react';
import { useForceAnimation } from '../hooks/useForceAnimation';

const Team = () => {
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
    rootMargin: '0px 0px -10% 0px',
  });

  const isVisible = useForceAnimation(inView);

  const processSteps = [
    {
      title: 'Initial Fit',
      description: 'We start by checking whether the business matches our focus on AI-related SaaS, digital products, and online operating models.',
      icon: <FileSearch size={40} className="text-primary-600" />,
      color: 'bg-blue-50',
      borderColor: 'border-blue-200',
    },
    {
      title: 'Product & Audience Review',
      description: 'We look at what the product does, who it serves, how it is sold, and what makes the business durable online.',
      icon: <Handshake size={40} className="text-primary-600" />,
      color: 'bg-green-50',
      borderColor: 'border-green-200',
    },
    {
      title: 'Transition Planning',
      description: 'If there is alignment, we talk through ownership change, founder involvement, and the practical requirements of a smooth handoff.',
      icon: <TrendingUp size={40} className="text-primary-600" />,
      color: 'bg-amber-50',
      borderColor: 'border-amber-200',
    },
    {
      title: 'Execution Roadmap',
      description: 'From there, the focus shifts to execution priorities that can strengthen the business after acquisition.',
      icon: <CheckCircle size={40} className="text-primary-600" />,
      color: 'bg-purple-50',
      borderColor: 'border-purple-200',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section id="team" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-block py-1 px-3 rounded-full bg-primary-100 text-primary-600 text-sm font-medium mb-4"
          >
            Our Process
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            How We Work Through Opportunities
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-lg text-gray-600 leading-relaxed"
          >
            Our process is designed for practical acquisition conversations around AI SaaS startups and online businesses.
          </motion.p>
        </div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {processSteps.map((step, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
              className={`group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border-t-4 ${step.borderColor}`}
            >
              <div className={`p-8 ${step.color}`}>
                <div className="flex justify-center">{step.icon}</div>
                <div className="text-center mt-4">
                  <span className="inline-block py-1 px-3 rounded-full bg-white text-primary-600 text-sm font-medium">
                    Step {index + 1}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-3 group-hover:text-primary-600 transition-colors text-center">{step.title}</h3>
                <p className="text-gray-600 text-center">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-16 max-w-3xl mx-auto text-center bg-white p-8 rounded-xl shadow-lg"
        >
          <h3 className="text-2xl font-bold mb-4">Ready to Discuss Your Business?</h3>
          <p className="text-gray-600 mb-6">
            Every opportunity is reviewed on its own merits. If you run an AI-related SaaS startup or online business and
            want to discuss an acquisition or strategic transition, that is the conversation we want this site to invite.
          </p>
          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href="#contact"
            className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-primary-600 text-white font-medium transition-colors hover:bg-primary-700"
          >
            Start the Conversation
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Team;
